<template>
  <div>
    <div class="mb-8 flex items-center justify-between">
      <h1 class="text-3xl font-bold">
        {{ pageTitle }}
      </h1>
      <Link
        class="btn-indigo"
        :href="newBlockRoute"
      >
        Dodaj blok
      </Link>
    </div>
    <div v-if="validBlocks.length === 0" class="rounded-lg border border-gray-200 bg-white p-12 text-center shadow-sm">
      <p class="text-gray-500">Nie znaleziono bloków.</p>
    </div>
    <div v-else class="space-y-4">
      <div
        v-for="block in validBlocks"
        :key="block.id"
        class="group overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:border-indigo-300 hover:shadow-md"
      >
        <div class="border-b border-gray-100 bg-white px-4 py-3">
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-3">
                <h3 class="text-base font-semibold text-gray-900 truncate">
                  {{ block.name }}
                </h3>
                <span class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  {{ block.template?.name || 'Brak szablonu' }}
                </span>
              </div>
              <div class="mt-1.5 flex items-center gap-3 text-xs text-gray-500">
                <span>Utworzono: {{ formatDate(block.created_at) }}</span>
                <span>•</span>
                <span>Zaktualizowano: {{ formatDate(block.updated_at) }}</span>
              </div>
            </div>

            <div class="ml-4 flex shrink-0 items-center gap-2">
              <Link
                v-if="block.id"
                :href="getEditBlockRoute(routes, block.site_context ?? props.site_context, block.id, block.kind)"
                class="btn-action-edit"
                title="Edytuj"
              >
                <icon name="edit" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                Edytuj
              </Link>
              <button
                v-if="block.id"
                type="button"
                class="btn-action-delete"
                title="Usuń"
                @click="confirmDelete(block)"
              >
                <icon name="trash" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                Usuń
              </button>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 p-2">
          <block-preview compact :block="blockForPreview(block)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import BlockPreview from '@/Shared/BlockPreview.vue';
import { getNewBlockRoute, getEditBlockRoute, getDestroyBlockRoute } from '@/utils/createBlockRouteHelper';
import { blockKindLabelPl } from '@/utils/blockKindLabels';

const { proxy } = getCurrentInstance();
const routes = proxy.$routes;

const props = defineProps({
  blocks: {
    type: Array,
    required: true,
  },
  kind: {
    type: String,
    default: null,
  },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: {
    type: String,
    default: null,
  },
});

const pageTitle = computed(() => blockKindLabelPl(props?.kind));

const validBlocks = computed(() => props.blocks.filter((b) => b && b.id != null));

const newBlockRoute = computed(() => getNewBlockRoute(routes, props.site_context, props.kind));

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const blockForPreview = (block) => ({
  id: block.id,
  name: block.name,
  kind: block.kind,
  data: block.data || {},
  images: block.images || {},
  template: block.template || null,
  site_context: block.site_context ?? props.site_context,
});

const confirmDelete = (block) => {
  if (
    confirm(
      `Czy na pewno chcesz usunąć blok „${block.name}”? Tej operacji nie można cofnąć.`,
    )
  ) {
    router.delete(
      getDestroyBlockRoute(routes, block.site_context ?? props.site_context, block.id, block.kind),
      { preserveScroll: true },
    );
  }
};
</script>
